import React, { FC, useContext, useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { WorkflowAutomationContext } from '../../../context/workflowAutomation.context';
import {
  ISelect,
  Loader,
  Switch,
  isHTML,
  useToastHook,
} from '@devd-client/devd/components';
import { getUserId } from '@devd-client/devd/auth';
import {
  DetailsHeading,
  DetailsSectionContainer,
  DetailsParagraph,
  Divider,
  ToggleButtonContainer,
  ToggleText,
  TagLabel,
  TagLabels,
} from './DetailSection.styled';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS, usePostAutomation } from '../../../apis';
import ReactHtmlParser from 'html-react-parser';
import { Workflow } from '../../../helpers/goals.types';

interface DetailsSectionProps {
  selectedTeam: ISelect;
}

const DetailsSection: FC<DetailsSectionProps> = ({ selectedTeam }) => {
  const {
    state: { clickedGoal },
  } = useContext(WorkflowAutomationContext);

  const [newToast] = useToastHook();

  const [activeToggle, setActiveToggle] = useState<boolean>(false);
  const [dataToSend, setDataToSend] = useState<Workflow | undefined>(undefined);

  const queryClient = useQueryClient();
  const { mutate: handleAutomationUpdate, isLoading } = usePostAutomation();

  useEffect(() => {
    setActiveToggle(clickedGoal?.enabled);
  }, [clickedGoal]);

  useEffect(() => {
    setDataToSend({ ...clickedGoal, team: selectedTeam.value });
  }, [clickedGoal, selectedTeam.value]);

  return (
    <>
      <DetailsSectionContainer>
        {clickedGoal?.tag?.length > 0 && (
          <TagLabels>
            {clickedGoal?.tag?.map((item: string) => (
              <TagLabel key={item}>{item}</TagLabel>
            ))}
          </TagLabels>
        )}
        <DetailsHeading>{clickedGoal?.name}</DetailsHeading>
        {isHTML(clickedGoal?.description) ? (
          <Box mt={3}>{ReactHtmlParser(clickedGoal?.description)}</Box>
        ) : (
          <DetailsParagraph>{clickedGoal?.description}</DetailsParagraph>
        )}

        <Divider />

        <ToggleButtonContainer>
          <ToggleText>Enable/Disable Automation</ToggleText>
          <Switch
            id="test-switch"
            toggled={activeToggle}
            disabled={!selectedTeam.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (!selectedTeam.value) {
                newToast({ message: 'Select any team', status: 'error' });
                return;
              }

              if (getUserId() === 'a867b5e7-ee22-4a9f-becd-b8f3aa43f753') {
                newToast({
                  message: "You don't have access.",
                  status: 'error',
                });
                return;
              }

              setActiveToggle(e.target.checked);

              handleAutomationUpdate(
                { ...dataToSend, enabled: e.target.checked },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries([QUERY_KEYS.workFlows]);
                    newToast({
                      message: `Automation settings updated successfully.`,
                      status: 'success',
                    });
                    e.target.checked === true &&
                      setTimeout(() => {
                        newToast({
                          message:
                            'We will begin capturing exceptions going forward.',
                          status: 'info',
                        });
                      }, 2000);
                  },
                  onError: (error: any) => {
                    setActiveToggle(!e.target.checked);
                    newToast({
                      message: `${error?.message}` || `Something went wrong.`,
                      status: 'error',
                    });
                  },
                }
              );
            }}
          />
        </ToggleButtonContainer>
      </DetailsSectionContainer>
      {isLoading && <Loader />}
    </>
  );
};

export default DetailsSection;
